/* The design's icons, inlined.
 *
 * Every path below is copied from the SVG the design's HTML carries at that
 * spot -- same viewBox, same stroke width, same caps and joins. They are
 * inline rather than <img src="icons/…"> so they inherit `currentColor`: the
 * chevron in a navy button and the chevron in a cream one are one component,
 * and neither needs a second file recoloured by hand.
 *
 * The TYPE icons stay as files (public/icons/type-*.svg) because the design
 * draws them at 40px in colour and they are artwork, not UI furniture.
 */

import egSvg from "flag-icons/flags/4x3/eg.svg?raw";
import saSvg from "flag-icons/flags/4x3/sa.svg?raw";

type IconProps = { size?: number; className?: string };

const base = (size: number) => ({
  width: size, height: size, viewBox: "0 0 24 24", fill: "none",
  stroke: "currentColor", strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const, "aria-hidden": true,
});

export const ChevronDown = ({ size = 14, className }: IconProps) => (
  <svg {...base(size)} strokeWidth={2} className={className}><path d="M6 9l6 6 6-6" /></svg>
);

export const Close = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} strokeWidth={2} className={className}><path d="M6 6l12 12M18 6L6 18" /></svg>
);

export const Menu = ({ size = 22, className }: IconProps) => (
  <svg {...base(size)} strokeWidth={2} className={className}><path d="M4 7h16M4 12h16M4 17h16" /></svg>
);

export const Search = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} strokeWidth={2.2} className={className}>
    <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
  </svg>
);

export const Pin = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} strokeWidth={2} className={className}>
    <path d="M12 21s-6-5.3-6-10a6 6 0 0 1 12 0c0 4.7-6 10-6 10z" /><circle cx="12" cy="11" r="2" />
  </svg>
);

export const Play = ({ size = 12, className }: IconProps) => (
  <svg {...base(size)} strokeWidth={2.2} className={className}>
    <path d="M7 5v14l12-7z" fill="currentColor" stroke="none" />
  </svg>
);

export const Alert = ({ size = 20, className }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="var(--sq-color-error)" strokeWidth={2} aria-hidden className={className}>
    <circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" />
  </svg>
);

/* The send arrow points the way the script runs: the design ships two files
 * for exactly this reason, and picking one by `dir` is the whole difference
 * between an arrow that means "send" and one that means "back". */
export const Send = ({ size = 18, rtl, className }: IconProps & { rtl: boolean }) => (
  <svg {...base(size)} strokeWidth={2.2} className={className}>
    {rtl ? <path d="M4 12h14M12 6l-6 6 6 6" /> : <path d="M4 12h14M12 6l6 6-6 6" />}
  </svg>
);

/* The pagination arrows. The design's RTL file draws "previous" as
 * `M9 6l6 6-6 6` -- pointing at the start of the line, which is the RIGHT in
 * Arabic and the LEFT in English. One icon that takes the direction, rather
 * than two that are correct in one script each. */
export const ChevronStart = ({ size = 16, rtl }: IconProps & { rtl: boolean }) => (
  <svg {...base(size)} strokeWidth={2}>
    {rtl ? <path d="M9 6l6 6-6 6" /> : <path d="M15 6l-6 6 6 6" />}
  </svg>
);

export const ChevronEnd = ({ size = 16, rtl }: IconProps & { rtl: boolean }) => (
  <svg {...base(size)} strokeWidth={2}>
    {rtl ? <path d="M15 6l-6 6 6 6" /> : <path d="M9 6l6 6-6 6" />}
  </svg>
);

/* FLAGS ARE REAL, SELF-HOSTED SVG (KAR-189). The design package draws them as
 * CSS placeholders -- three stripes for Egypt, a green block for Saudi Arabia --
 * which are not the countries' flags. These are the flag-icons package's, which
 * carry the Eagle of Saladin and the shahada with the sword. They are bundled
 * as strings at build time: no CDN, no request at runtime.
 *
 * The 20x14 footprint and --sq-radius-xs are set inline so they hold on every
 * screen that draws a flag, inside the .sq-v3 wrapper or not. The artwork is
 * 4:3 and the footprint is ~10:7, so it is cropped a few pixels top and bottom
 * (xMidYMid slice) rather than stretched.
 *
 * The package's ids are already namespaced (eg-a, sa-a), so the same flag
 * drawn in the header, the menu and the footer cannot collide with another. */
const FLAG_SVG: Record<string, string> = {
  EG: egSvg,
  SA: saSvg,
};

const asIcon = (svg: string) => svg.replace(
  /<svg[^>]*?viewBox="([^"]+)"[^>]*>/,
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="$1" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false" style="display:block">',
);

const FLAG_HTML = Object.fromEntries(Object.entries(FLAG_SVG).map(([k, v]) => [k, asIcon(v)]));

export const Flag = ({ code }: { code: string }) => {
  const key = code.toUpperCase();
  const cls = `sq-flag sq-flag--${code.toLowerCase()}`;
  const html = FLAG_HTML[key];
  const box: React.CSSProperties = {
    width: 20, height: 14, flex: "none", display: "inline-block", overflow: "hidden",
    borderRadius: "var(--sq-radius-xs)", verticalAlign: "middle",
  };
  /* A market with no artwork yet keeps the empty box it always had. */
  return html
    ? <span className={cls} style={box} aria-hidden="true" dangerouslySetInnerHTML={{ __html: html }} />
    : <span className={cls} aria-hidden="true" />;
};
